/**
 * API de usuarios y roles de un tenant (super-admin).
 */
import { apiFetch } from "./client";

export interface Usuario {
  id: number;
  /** Persona dueña de la cuenta. Une esta cuenta con su ficha profesional. */
  persona_id: number | null;
  /**
   * Quién es de verdad. El `username` es el puesto —"Recepción", "Caja"— y no
   * identifica a nadie: la persona detrás de esa cuenta tiene nombre y DNI.
   */
  persona_nombre: string | null;
  persona_documento: string | null;
  username: string | null;
  email: string;
  mfa_habilitado: boolean;
  activo: boolean;
  is_super_admin: boolean;
  ultimo_login: string | null;
  created_at: string;
  rol_ids: number[];
}

export interface UsuarioCreate {
  username?: string | null;
  email: string;
  password: string;
  persona_id?: number | null;
  rol_ids?: number[];
}

export interface UsuarioUpdate {
  email?: string;
  activo?: boolean;
  rol_ids?: number[];
}

export interface PermisoMini {
  id: number;
  codigo: string;
  descripcion: string;
  categoria: string;
}

export interface Rol {
  id: number;
  codigo: string;
  nombre: string;
  es_sistema: boolean;
  permisos: PermisoMini[];
}

// ---------- El personal del policlínico ----------
//
// Aquí vivían además cuatro funciones que pegaban contra
// `/v1/policlinicos/{id}/usuarios`, del tiempo en que un super-admin
// administraba varios policlínicos desde fuera. Esas rutas ya no existen en el
// servidor y ninguna pantalla las llamaba: eran código muerto que sobrevivió a
// la eliminación del multi-tenant, y lo peor de un código así es que un día
// alguien lo usa creyendo que funciona.

/**
 * Lista las cuentas de acceso del tenant actual. Requiere usuario:read.
 *
 * `soloPersonal` deja fuera a quien solo tiene la cuenta del portal del
 * paciente. Se pide explícitamente porque hay una pantalla —Roles— que sí
 * necesita contarlos a todos.
 */
export function listUsuarios(
  opts: { soloPersonal?: boolean } = {},
): Promise<Usuario[]> {
  const q = opts.soloPersonal ? "?solo_personal=true" : "";
  return apiFetch<Usuario[]>(`/v1/usuarios${q}`);
}

/** Crea una cuenta de personal en el tenant actual. Requiere usuario:create. */
export function createUsuario(payload: UsuarioCreate): Promise<Usuario> {
  return apiFetch<Usuario>("/v1/usuarios", { method: "POST", body: payload });
}

/** Actualiza una cuenta de personal del tenant actual. Requiere usuario:update. */
export function updateUsuario(userId: number, payload: UsuarioUpdate): Promise<Usuario> {
  return apiFetch<Usuario>(`/v1/usuarios/${userId}`, { method: "PATCH", body: payload });
}

/** Lista los roles asignables del tenant actual. Requiere rol:read. */
export function listRoles(): Promise<Rol[]> {
  return apiFetch<Rol[]>("/v1/roles");
}

// ---------- Roles y permisos del tenant actual ----------

export interface Permiso {
  id: number;
  codigo: string;
  descripcion: string;
  /** Agrupador para la matriz de la pantalla de Roles (pacientes, caja, …). */
  categoria: string;
}

export interface RolCreate {
  /** minúsculas, dígitos y guion bajo. Es el identificador estable del rol. */
  codigo: string;
  nombre: string;
  permiso_codigos: string[];
}

export interface RolUpdate {
  nombre?: string;
  permiso_codigos?: string[];
}

/** Catálogo completo de permisos del sistema. Requiere rol:read. */
export function listPermisos(): Promise<Permiso[]> {
  return apiFetch<Permiso[]>("/v1/permisos");
}

export function createRol(payload: RolCreate): Promise<Rol> {
  return apiFetch<Rol>("/v1/roles", { method: "POST", body: payload });
}

export function updateRol(rolId: number, payload: RolUpdate): Promise<Rol> {
  return apiFetch<Rol>(`/v1/roles/${rolId}`, { method: "PATCH", body: payload });
}

/** Falla con 409 si alguna cuenta todavía tiene el rol. */
export function deleteRol(rolId: number): Promise<void> {
  return apiFetch<void>(`/v1/roles/${rolId}`, { method: "DELETE" });
}
